import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useAuth from '@/hooks/useAuth';

import SendInvitationsModal from '../../components/modals/SendInvitationsModal';
import ShiftWorkersModal, {
  ShiftWorkerRow,
} from '../../components/modals/ShiftWorkersModal';
import { ErrorText, MutedText } from '../../styles';
import { JobDraftV2 } from '../../context';
import OTRiskBanner from '@/routes/Agency/shared/OTRiskBanner';
import HoursThisWeekCell from '@/routes/Agency/shared/HoursThisWeekCell';
import { sumCurrentWeekShiftHours } from '@/routes/Agency/shared/currentWeekRange';
import useOvertimeGate, { type OvertimeOutcome } from '@/hooks/useOvertimeGate';
import { createOrderV2Client } from '../../api/client';
import {
  BillingAccountV2,
  CertificateV2,
  SkillListItem,
  WorkerCounts,
  WorkerListItem,
  WorkerSearchParams,
  WorkerTab,
} from '../../api/types';

import {
  ActionButtonsCell,
  AutoFillCard,
  AutoFillText,
  AvatarOverflowBubble,
  AvatarPlaceholder,
  BadgeDropdownPopover,
  BadgeDropdownTrigger,
  BadgeDropdownTriggerLabel,
  BadgeDropdownWrap,
  BadgeOptionRow,
  BreakText,
  CertificateCard,
  CertificateTitle,
  Checkbox,
  ChoiceBody,
  ChoiceCard,
  ChoiceDescription,
  ChoiceIcon,
  ChoiceRadio,
  ChoiceSection,
  ChoiceTitle,
  ConflictBadge,
  ContentWrap,
  DateCard,
  DateCardHeader,
  DateCardsRow,
  DateLabel,
  DowDot,
  DowRow,
  HeaderRow,
  InviteAvatar,
  InviteHeader,
  InvitedRow,
  PageButton,
  PaginationRow,
  PanelTitle,
  PanelTitleRow,
  SearchIconAbs,
  SearchInput,
  SearchWrap,
  SelectAllRow,
  SelectionPill,
  SelectionPillBadge,
  ShiftStatCell,
  ShiftsCard,
  ShiftsHeaderRow,
  StepContent,
  StepTitle,
  TabButton,
  TabsRow,
  TimeText,
  ToggleKnob,
  ToggleSwitch,
  WorkerAvatar,
  WorkerCell,
  WorkerMetaRow,
  WorkerName,
  WorkerPanel,
  WorkerRow,
  WorkerTextStack,
} from './styles';

type StaffingStrategy = NonNullable<JobDraftV2['staffing_strategy']>;

// Base tabs visible to every admin. The "Others" tab is conditionally
// appended below for tenant admins only — legacy v1's `getTabs` adds it
// behind a `!isClientAdmin` check (see CreateGigOrder StaffingUtils),
// because the bucket only makes sense outside the customer-scoped view.
const BASE_WORKER_TABS: Array<{ label: string; value: WorkerTab }> = [
  { label: 'All', value: 'all' },
  { label: 'Favorites', value: 'favourites' },
  { label: 'Highly rated', value: 'highly_rated' },
  { label: 'Past workers', value: 'past_workers' },
];
const OTHERS_WORKER_TAB: { label: string; value: WorkerTab } = {
  label: 'Others',
  value: 'others',
};
// "GravyTrained" is gated by the currently selected skill: v1's getTabs
// inserts it after Favorites only when `skill.gravyTrained` is truthy.
// We mirror that placement below.
const TRAINED_WORKER_TAB: { label: string; value: WorkerTab } = {
  label: 'GravyTrained',
  value: 'gravy_trained',
};

const PAGE_SIZE = 15;

type StaffingStepProps = {
  orderType: 'GIG' | 'LTA' | null;
  billingAccount: BillingAccountV2 | null;
  jobs: JobDraftV2[];
  onUpdateJobs: (jobs: JobDraftV2[]) => void;
  skills?: SkillListItem[];
  // 'choice' renders the strategy cards only. 'invite' renders the shifts +
  // worker list shown after the user picks "Send Invitations" and clicks
  // "Continue to Invite". Parent owns this state so the wizard footer can
  // swap its button label/handler in lockstep.
  substep?: 'choice' | 'invite';
  // Pickable badge catalog for this billing account (mirrors legacy
  // `agency.certificates` filtered to BADGE type). Customer defaults are
  // already auto-seeded onto `jobs[0].certificates` by the parent.
  certificateOptions?: CertificateV2[];
  // GRAVY-10073: increments the parent's `staffingOTCounters` ref so the
  // Vexo `step_completed` aggregates carry non-zero values. Fired by the
  // OT gate at three points: modal shown, admin confirmed, admin dismissed.
  onOvertimeOutcome?: (kind: 'shown' | 'accepted' | 'dismissed') => void;
};

// Mirrors v1's `workerName` helper (util/worker.ts): always includes the
// middle name when present, falling back to "Worker #<id>" if the user
// payload is missing entirely.
const formatName = (worker: WorkerListItem) => {
  const first = worker.user?.firstName || '';
  const middle = worker.user?.middleName || '';
  const last = worker.user?.lastName || '';
  const composed = [first, middle, last].filter(Boolean).join(' ').trim();
  return composed || `Worker #${worker.id}`;
};

const initialFor = (worker: WorkerListItem) => {
  const first = worker.user?.firstName?.trim() || '';
  const last = worker.user?.lastName?.trim() || '';
  return (first[0] || last[0] || '?').toUpperCase();
};

const isTruthyFlag = (v: unknown): boolean => v === true || v === 1;

// Mirrors v1's Avatar component (components/Avatar.tsx): if the configured
// `src` 404s or otherwise fails to decode, fall back to the worker's
// initial rather than leaving an empty circle. v2 previously just hid the
// `<img>` on error, so a worker with a stale avatarUrl rendered as a blank
// avatar — see the "worker profile photo was not displayed" bug.
const WorkerAvatarBubble = ({
  worker,
  size,
  title,
}: {
  worker: WorkerListItem;
  size: 'lg' | 'sm';
  title?: string;
}) => {
  const [isError, setIsError] = useState(false);
  const showImage = !!worker.avatarUrl && !isError;
  const initial = initialFor(worker);
  const content = showImage ? (
    <img
      src={worker.avatarUrl as string}
      alt=""
      onError={() => setIsError(true)}
    />
  ) : (
    <span>{initial}</span>
  );
  return size === 'lg' ? (
    <WorkerAvatar aria-hidden title={title}>
      {content}
    </WorkerAvatar>
  ) : (
    <InviteAvatar aria-hidden title={title}>
      {content}
    </InviteAvatar>
  );
};

const StarIcon = ({ filled }: { filled: boolean }) => (
  <svg
    viewBox="0 0 20 20"
    width={16}
    height={16}
    aria-hidden
    fill={filled ? '#FAAF00' : '#D1D5DB'}
  >
    <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.2 1 5.9L10 14.9 4.8 17.8l1-5.9L1.5 7.7l5.9-.9L10 1.5z" />
  </svg>
);

const FavoriteIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width={14}
    height={14}
    aria-label="Favorite worker"
    role="img"
  >
    <path
      d="M12 21s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 6.5a4.3 4.3 0 0 1 7.5 4.3C19.5 16.4 12 21 12 21z"
      fill="#45A735"
    />
  </svg>
);

const OpenJobIcon = () => (
  <svg viewBox="0 0 60 60" width={60} height={60} fill="none">
    <circle
      cx="30"
      cy="30"
      r="28"
      stroke="#44A735"
      strokeWidth="1.5"
      fill="#F5FFF4"
    />
    <path
      d="M20 22h20v18a4 4 0 0 1-4 4H24a4 4 0 0 1-4-4V22z"
      stroke="#44A735"
      strokeWidth="1.5"
      fill="#fff"
    />
    <path d="M26 22v-4a4 4 0 1 1 8 0v4" stroke="#44A735" strokeWidth="1.5" />
    <circle cx="30" cy="32" r="2" fill="#44A735" />
  </svg>
);

const InviteIcon = () => (
  <svg viewBox="0 0 60 60" width={60} height={60} fill="none">
    <circle
      cx="30"
      cy="30"
      r="28"
      stroke="#44A735"
      strokeWidth="1.5"
      fill="#F5FFF4"
    />
    <rect
      x="16"
      y="22"
      width="28"
      height="18"
      rx="2"
      stroke="#44A735"
      strokeWidth="1.5"
      fill="#fff"
    />
    <path d="M16 24l14 10 14-10" stroke="#44A735" strokeWidth="1.5" />
  </svg>
);

const SearchIconSvg = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ADADAD"
    strokeWidth={2}
    width={16}
    height={16}
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const CheckmarkIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth={2.5}
    width={14}
    height={14}
  >
    <polyline points="4 12 9 17 20 7" />
  </svg>
);

const DashIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth={2.5}
    width={14}
    height={14}
  >
    <line x1="6" y1="12" x2="18" y2="12" />
  </svg>
);

const DOW_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const;

const dowIndex = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return -1;
  // Date#getDay returns 0=Sunday, 6=Saturday — convert to 0=Mon ... 6=Sun.
  const day = d.getDay();
  return (day + 6) % 7;
};

const formatMonthDay = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatHm = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const StaffingStep = ({
  orderType,
  billingAccount,
  jobs,
  onUpdateJobs,
  skills,
  substep = 'choice',
  certificateOptions = [],
  onOvertimeOutcome,
}: StaffingStepProps) => {
  const { check: checkOvertime, modal: overtimeModal } = useOvertimeGate();
  const [tab, setTab] = useState<WorkerTab>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [workers, setWorkers] = useState<WorkerListItem[]>([]);
  const [counts, setCounts] = useState<WorkerCounts>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  // Index of the shift whose V1-style worker drill-in modal is open
  // (null = closed). Set by clicking the hired/invited summary on a
  // shift card; the modal lists that shift's workers and cancels
  // hires/invites scoped to this shift only.
  const [workerModalShift, setWorkerModalShift] = useState<number | null>(null);
  // Indexes of shifts the user has deselected on the invite sub-screen.
  // Empty set = "All N shifts selected" (the prototype default). The
  // selection is mirrored onto `jobs[0].unselected_shift_indexes` so it
  // survives navigation between wizard steps and propagates to
  // buildCreatePayload at submit (V1 parity: hires only land on
  // selected schedules).
  const [unselectedShifts, setUnselectedShifts] = useState<Set<number>>(
    () => new Set(jobs[0]?.unselected_shift_indexes ?? []),
  );
  // Cache of worker payloads keyed by id for any worker the admin has
  // invited or hired in this session. The wizard's flat invite_workers /
  // hire_workers arrays only carry ids; the cache lets the per-shift
  // "Invited" badge render an avatar/initial for each worker even after
  // the worker scrolls out of the paginated list.
  const invitedWorkersRef = useRef<Map<number, WorkerListItem>>(new Map());

  const { currentAdminIsCustomerAdmin: isClientAdmin } = useAuth();
  const isLta = orderType === 'LTA';
  // Match legacy v1 parity: tenant admins see an "Others" tab (system-wide
  // worker search), customer admins do not. The "GravyTrained" tab is
  // gated by `skill.gravy_trained` and slotted right after Favorites,
  // mirroring v1's getTabs() insertion order in
  // CreateGigOrder/.../StaffingUtils.tsx.
  const selectedSkillIsTrained = useMemo(() => {
    const job = jobs[0];
    if (!job?.skill_id || !skills) return false;
    return !!skills.find((s) => s.id === job.skill_id)?.gravy_trained;
  }, [jobs, skills]);
  const workerTabs = useMemo(() => {
    const base = [...BASE_WORKER_TABS];
    if (selectedSkillIsTrained) {
      // Insert immediately after Favorites (index 2) to match v1 ordering.
      base.splice(2, 0, TRAINED_WORKER_TAB);
    }
    return isClientAdmin ? base : [...base, OTHERS_WORKER_TAB];
  }, [isClientAdmin, selectedSkillIsTrained]);
  // If the selected tab disappears (e.g. user toggles between admin roles
  // mid-session, or starts on Others and the auth context resolves to a
  // client admin), snap back to "All" so we never submit an unsupported tab.
  useEffect(() => {
    if (!workerTabs.some((entry) => entry.value === tab)) {
      setTab('all');
    }
  }, [workerTabs, tab]);
  const job = jobs[0];
  const strategy: StaffingStrategy | null = isLta
    ? 'SEND_INVITATIONS'
    : (job?.staffing_strategy ?? null);

  // Mirrors v1's `isLTA || allSchedulesArePast()` guard on the Hire button:
  // a Client Admin can only hire when the order is LTA or every shift has
  // already started. Otherwise the Hire column stays empty for that row.
  const allShiftsArePast = useMemo(() => {
    const list = job?.shifts || [];
    if (list.length === 0) return false;
    const now = Date.now();
    return list.every((shift) => {
      const t = new Date(shift.start_at).getTime();
      return Number.isFinite(t) && t < now;
    });
  }, [job?.shifts]);
  const canShowHireButton = !isClientAdmin || isLta || allShiftsArePast;
  // V1 passes `isLTA={isLTA || allSchedulesArePast()}` into the row, and
  // the row keeps Invite hidden when that flag is true (unless there's a
  // conflict). Mirror that here so past GIG jobs behave like LTA — Hire
  // shows, Invite is suppressed.
  const treatAsLtaForInvite = isLta || allShiftsArePast;

  // Always-latest snapshot of the `jobs` prop. Multiple toggles
  // (deselect shift, invite worker, hire worker, toggle badge) write
  // through `onUpdateJobs`, and `actions.setJobs` REPLACES the whole
  // array. If two mutations happen back-to-back before React re-
  // renders, the second handler can capture the stale `jobs` from its
  // render-time closure and silently clobber the first write. Reading
  // through this ref makes every mutation build on the freshest
  // version, even mid-batch — the specific repro: deselect a shift
  // then immediately click Invite, the Invite write was discarding
  // the deselect because it spread the pre-deselect `jobs`.
  const jobsRef = useRef(jobs);
  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  const setStrategy = (next: StaffingStrategy) => {
    if (isLta) return;
    const nextJobs = [...jobsRef.current];
    nextJobs[0] = { ...nextJobs[0], staffing_strategy: next };
    onUpdateJobs(nextJobs);
  };

  const inviteIds = useMemo(
    () => job?.invite_workers || [],
    [job?.invite_workers],
  );
  const hireIds = useMemo(() => job?.hire_workers || [], [job?.hire_workers]);
  const certificateIds = useMemo(
    () => job?.certificates || [],
    [job?.certificates],
  );

  // V1 parity: when the admin deselects every shift, staffing
  // assignment must be blocked — legacy `StaffingShiftsCardsSection`
  // disabled the worker row Hire/Invite controls (and the
  // Continue button) until at least one schedule was selected. V2 used
  // to leave the worker rows fully interactive, so an admin could
  // accumulate invites/hires that wouldn't apply to any shift.
  const totalShiftsCount = job?.shifts?.length || 0;
  const noShiftsSelected =
    totalShiftsCount === 0 || unselectedShifts.size === totalShiftsCount;

  const workerShiftScope = useMemo(
    () => job?.worker_shift_scope ?? {},
    [job?.worker_shift_scope],
  );

  // The shift indexes selected *right now*. Captured as a snapshot onto
  // `worker_shift_scope[workerId]` at the moment of a Hire/Invite so a
  // later shift (re)selection can't retroactively expand that assignment.
  const selectedShiftIndexes = useMemo(() => {
    const result: number[] = [];
    for (let i = 0; i < totalShiftsCount; i += 1) {
      if (!unselectedShifts.has(i)) result.push(i);
    }
    return result;
  }, [totalShiftsCount, unselectedShifts]);

  // GRAVY-10073: total hours of the shifts the admin has selected,
  // **net of per-shift mandatory break minutes** so the OT-risk banner
  // and the per-row at-risk highlighting agree with the backend
  // projection's net-of-break accounting. Each shift's break comes from
  // `ShiftDraftV2.break_minutes`; missing/0 treats as gross. `Math.max`
  // clamps the (unusual) case where break > gross.
  const selectedShiftHours = useMemo(() => {
    const list = job?.shifts || [];
    let total = 0;
    for (const i of selectedShiftIndexes) {
      const s = list[i];
      if (!s) continue;
      const startMs = new Date(s.start_at).getTime();
      const endMs = new Date(s.end_at).getTime();
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) continue;
      const breakHours = (s.break_minutes ?? 0) / 60;
      const netHours = (endMs - startMs) / 3_600_000 - breakHours;
      total += Math.max(netHours, 0);
    }
    return Number(total.toFixed(2));
  }, [job?.shifts, selectedShiftIndexes]);

  // GRAVY-10099: payload for the OT gate when an admin invites/hires a
  // worker. The wizard has no persisted job_id yet, so the gate falls
  // back to `/api/admin/overtime_projection_from_shifts` with the
  // currently-selected non-past shifts as the proposed work. Past-start
  // shifts are excluded — they're never new bookings.
  //
  // GRAVY-10073 break-aware: each shift carries its own
  // `mandatory_break_minutes` (V2's `ShiftDraftV2.break_minutes`). The
  // backend subtracts it when bucketing per-week hours so the projection
  // matches payroll-OT semantics.
  const proposedShiftsForGate = useMemo(() => {
    const list = job?.shifts || [];
    const now = Date.now();
    const result: Array<{
      start_at: string;
      end_at: string;
      mandatory_break_minutes: number;
    }> = [];
    for (const i of selectedShiftIndexes) {
      const s = list[i];
      if (!s) continue;
      const startMs = new Date(s.start_at).getTime();
      if (!Number.isFinite(startMs) || startMs < now) continue;
      result.push({
        start_at: s.start_at,
        end_at: s.end_at,
        mandatory_break_minutes: s.break_minutes ?? 0,
      });
    }
    return result;
  }, [job?.shifts, selectedShiftIndexes]);

  // GRAVY-10073 follow-up: per-row at-risk highlighting is now action-
  // oriented — it only flips red when the admin's CURRENT-WEEK selection
  // would push the worker over 40 *this week*. So we feed
  // `HoursThisWeekCell` only the subset of selected shifts that fall in
  // the worker's current UTC week (rather than the cumulative
  // `selectedShiftHours` used for the banner's total-duration display).
  // Without this filter, staffing a future-week job flips every 40/40
  // worker red even though their OT projection is unaffected.
  const currentWeekSelectedShiftHours = useMemo(() => {
    const list = job?.shifts || [];
    const inSelected: Array<{
      start_at: string;
      end_at: string;
      mandatory_break_minutes: number;
    }> = [];
    for (const i of selectedShiftIndexes) {
      const s = list[i];
      if (s) {
        inSelected.push({
          start_at: s.start_at,
          end_at: s.end_at,
          mandatory_break_minutes: s.break_minutes ?? 0,
        });
      }
    }
    return sumCurrentWeekShiftHours(inSelected);
  }, [job?.shifts, selectedShiftIndexes]);

  // Every shift index for the job. A worker whose `worker_shift_scope`
  // entry is missing (template-prefilled / legacy drafts) is treated as
  // applying to *all* shifts — matching the `scope == null` fallback
  // already used by filledForShift / workersForShift / the
  // `workerAppliesToShift` rule in buildCreatePayload.
  const allShiftIndexes = useMemo(
    () => Array.from({ length: totalShiftsCount }, (_, i) => i),
    [totalShiftsCount],
  );

  // The shift indexes a worker is currently assigned to for the given
  // list. `null` means "every shift" (missing scope snapshot); `[]`
  // means the worker is not in the list at all.
  const assignedShiftsFor = useCallback(
    (id: number, ids: number[]): number[] | null => {
      if (!ids.includes(id)) return [];
      return workerShiftScope[id] ?? null;
    },
    [workerShiftScope],
  );

  // V1 parity (`checkWorkerHiredOrInvited` in MainInviteWorkersPage):
  // the worker row reflects ONLY the currently-selected shift context.
  // The "Hired"/"Invited" pill shows iff the worker is assigned to
  // *every* currently-selected shift — never as a flat job-level
  // membership. So hiring for Shift 1 and then also selecting Shift 2
  // correctly leaves the row as "Hire" for the (still-unstaffed) Shift
  // 2 context, instead of falsely showing the job-level "Hired" state.
  const isAssignedToAllSelected = useCallback(
    (id: number, ids: number[]) => {
      if (selectedShiftIndexes.length === 0) return false;
      const assigned = assignedShiftsFor(id, ids);
      if (assigned === null) return ids.includes(id);
      return selectedShiftIndexes.every((i) => assigned.includes(i));
    },
    [selectedShiftIndexes, assignedShiftsFor],
  );

  const toggleCertificate = (id: number) => {
    const next = certificateIds.includes(id)
      ? certificateIds.filter((existing) => existing !== id)
      : [...certificateIds, id];
    const nextJobs = [...jobsRef.current];
    nextJobs[0] = { ...nextJobs[0], certificates: next };
    onUpdateJobs(nextJobs);
  };

  // Badge Requirements dropdown (Tenant-admin redesign). Local open state
  // + click-outside dismiss matches the EntryScreen client/billing
  // dropdown pattern; the selection itself stays on `job.certificates`.
  const [badgeOpen, setBadgeOpen] = useState(false);
  const badgeWrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!badgeOpen) return;
    const onDown = (event: MouseEvent) => {
      if (
        badgeWrapRef.current &&
        !badgeWrapRef.current.contains(event.target as Node)
      ) {
        setBadgeOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [badgeOpen]);

  const updateSelection = (changes: Partial<JobDraftV2>) => {
    const nextJobs = [...jobsRef.current];
    nextJobs[0] = { ...nextJobs[0], ...changes };
    onUpdateJobs(nextJobs);
  };

  // Mirrors v1's PRIVATE + publish:true semantics — persisted on the job
  // draft so `buildCreatePayload` can emit `publish` in the REST payload.
  const autoFill = job?.auto_fill === true;
  const setAutoFill = (next: boolean) => updateSelection({ auto_fill: next });

  const cacheWorker = (worker: WorkerListItem) => {
    invitedWorkersRef.current.set(worker.id, worker);
  };

  // Apply a per-shift assignment change to `worker_shift_scope`,
  // mirroring v1's additive, per-schedule semantics
  // (`inviteButtonHandler` / `getHiredUpdatedSchedules` in
  // MainInviteWorkersPage). Assigning UNIONS the target shift indexes
  // onto any existing assignment so a later shift (re)selection never
  // wipes a prior-shift hire/invite. Removing subtracts ONLY the
  // currently-selected shifts, so assignments on other shifts stay
  // intact unless the admin explicitly removes the worker there too.
  // `existing === null` means the prior scope covered every shift
  // (legacy / template draft); pass `[]` when the worker is switching
  // lists (invite -> hire) so the old list's snapshot can't leak across.
  const applyScopeChange = (
    id: number,
    assigning: boolean,
    targetIndexes: number[],
    existing: number[] | null,
  ): { scope: Record<number, number[]>; stillAssigned: boolean } => {
    const next: Record<number, number[]> = { ...workerShiftScope };
    const base = existing === null ? allShiftIndexes : existing;
    if (assigning) {
      next[id] = Array.from(new Set([...base, ...targetIndexes]));
      return { scope: next, stillAssigned: true };
    }
    const remaining = base.filter((i) => !targetIndexes.includes(i));
    if (remaining.length === 0) {
      delete next[id];
      return { scope: next, stillAssigned: false };
    }
    next[id] = remaining;
    return { scope: next, stillAssigned: true };
  };

  // V1 parity (`handleCancelSelected` in ShiftWorkersModal): cancel a
  // hire/invite from the shift drill-in modal. Removes ONLY the given
  // shift index from each worker's scope — assignments on other shifts
  // stay intact (the admin must explicitly cancel those too). A worker
  // with no scope snapshot (template/legacy draft) is treated as
  // covering every shift, matching applyScopeChange / workersForShift.
  // When a worker's last in-scope shift is cancelled they drop out of
  // the flat hire/invite list entirely.
  const cancelWorkersForShift = (
    list: 'hired' | 'invited',
    workerIds: number[],
    shiftIndex: number,
  ) => {
    const idsField = list === 'hired' ? 'hire_workers' : 'invite_workers';
    const currentIds = list === 'hired' ? hireIds : inviteIds;
    const scope: Record<number, number[]> = { ...workerShiftScope };
    let remainingIds = [...currentIds];
    workerIds.forEach((id) => {
      const existing = scope[id] ?? null;
      const base = existing === null ? allShiftIndexes : existing;
      const remaining = base.filter((i) => i !== shiftIndex);
      if (remaining.length === 0) {
        delete scope[id];
        remainingIds = remainingIds.filter((w) => w !== id);
      } else {
        scope[id] = remaining;
      }
    });
    updateSelection({ [idsField]: remainingIds, worker_shift_scope: scope });
  };

  // GRAVY-10099: gates an invite/hire toggle behind the W2 over-40 OT
  // warning. The gate short-circuits via the hook when the projection
  // is under 40 or when the worker isn't W2, so this matches v1's per-
  // toggle behavior for non-OT cases — no visible flicker, no API call
  // beyond what the gate runs internally.
  //
  // `proceed` is the existing state-mutation body. We bypass the gate
  // when the admin is REMOVING (toggling off) — un-inviting can't
  // worsen the worker's OT picture — or when there are no proposed
  // shifts to project against (the wizard footer's button is also
  // disabled in that state; this is defense-in-depth).
  const gateToggle = (
    worker: WorkerListItem,
    mode: 'invite' | 'hire',
    removing: boolean,
    proceed: () => void,
  ) => {
    if (removing || proposedShiftsForGate.length === 0) {
      proceed();
      return;
    }
    checkOvertime(
      null,
      worker,
      mode,
      (outcome: OvertimeOutcome) => {
        if (outcome.triggered) {
          onOvertimeOutcome?.('shown');
          onOvertimeOutcome?.('accepted');
        }
        proceed();
      },
      {
        proposedShifts: proposedShiftsForGate,
        onCancel: () => {
          onOvertimeOutcome?.('shown');
          onOvertimeOutcome?.('dismissed');
        },
      },
    );
  };

  const toggleInvite = (worker: WorkerListItem) => {
    // Defense in depth: the per-row button is also `disabled` when
    // every shift has been deselected, but the click handler guards
    // against keyboard-triggered actions or any future un-disabled
    // surface from sneaking invitations through.
    if (noShiftsSelected) return;
    const id = worker.id;
    cacheWorker(worker);
    const wasInInvite = inviteIds.includes(id);
    // The row only reads "Invited" when the worker is invited for
    // *every* currently-selected shift (v1 parity). Clicking it then
    // removes them from exactly those shifts; otherwise the click
    // invites them for the selected shifts while leaving any
    // other-shift assignment untouched.
    const removing = isAssignedToAllSelected(id, inviteIds);

    gateToggle(worker, 'invite', removing, () => {
      // v1 invitations apply to all currently-selected schedules.
      const { scope, stillAssigned } = applyScopeChange(
        id,
        !removing,
        selectedShiftIndexes,
        wasInInvite ? (workerShiftScope[id] ?? null) : [],
      );
      const nextInvite = stillAssigned
        ? wasInInvite
          ? inviteIds
          : [...inviteIds, id]
        : inviteIds.filter((w) => w !== id);
      updateSelection({
        invite_workers: nextInvite,
        hire_workers: hireIds.filter((w) => w !== id),
        worker_shift_scope: scope,
      });
    });
  };

  const toggleHire = (worker: WorkerListItem) => {
    if (noShiftsSelected) return;
    const id = worker.id;
    cacheWorker(worker);
    const wasInHire = hireIds.includes(id);
    // "Hired" shows only when the worker is hired for every selected
    // shift (v1 parity); clicking it then unhires those shifts only.
    const removing = isAssignedToAllSelected(id, hireIds);

    gateToggle(worker, 'hire', removing, () => {
      // Scope a *new* hire to the still-open selected shifts so it fills
      // an actually-unfilled position (e.g. a re-selected Shift 1) rather
      // than piling onto an already-full Shift 2 (GRAVY-10062). Fall back
      // to all selected shifts when no shift declares a positive quantity,
      // preserving the pre-fix behavior for legacy / quantity-less drafts.
      const hireScopeIndexes = openSelectedShiftIndexes.length
        ? openSelectedShiftIndexes
        : selectedShiftIndexes;
      const targetIndexes = removing ? selectedShiftIndexes : hireScopeIndexes;
      const { scope, stillAssigned } = applyScopeChange(
        id,
        !removing,
        targetIndexes,
        wasInHire ? (workerShiftScope[id] ?? null) : [],
      );
      const nextHire = stillAssigned
        ? wasInHire
          ? hireIds
          : [...hireIds, id]
        : hireIds.filter((w) => w !== id);
      updateSelection({
        hire_workers: nextHire,
        invite_workers: inviteIds.filter((w) => w !== id),
        worker_shift_scope: scope,
      });
    });
  };

  const jobSchedulesParam = useMemo(() => {
    const shifts = (job?.shifts || []).map((shift) => ({
      dateRange: { startDate: shift.start_at, endDate: shift.start_at },
      startTime: shift.start_at.slice(11, 16),
      endTime: shift.end_at.slice(11, 16),
    }));
    return shifts.length > 0 ? JSON.stringify(shifts) : undefined;
  }, [job?.shifts]);

  const baseParams: WorkerSearchParams | null = useMemo(() => {
    if (!billingAccount) return null;
    return {
      billing_account_id: billingAccount.id,
      skill_id: job?.skill_id ?? null,
      address_id: job?.address_id ?? null,
      worker_type: isLta ? 'lta' : 'gig',
      tax_type: billingAccount.tax_type || 'all',
      background_check_required: !!billingAccount.require_background_checks,
      job_schedules: jobSchedulesParam,
      // Mirrors legacy `badgeIds` query param: when the admin has picked
      // certificate badges, restrict the worker list to workers that hold
      // them (only relevant on the invite sub-screen).
      badge_ids: certificateIds.length > 0 ? certificateIds : undefined,
    };
  }, [
    billingAccount,
    isLta,
    job?.skill_id,
    job?.address_id,
    jobSchedulesParam,
    certificateIds,
  ]);

  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedSearch(search.trim()),
      250,
    );
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [
    tab,
    debouncedSearch,
    billingAccount?.id,
    job?.skill_id,
    job?.address_id,
  ]);

  // The worker list only renders on the invite sub-screen — the choice
  // screen shows the two strategy cards and nothing else, even after the
  // user has selected "Send Invitations".
  const showWorkerPanel =
    substep === 'invite' && strategy === 'SEND_INVITATIONS';

  useEffect(() => {
    if (!baseParams || !showWorkerPanel) {
      setWorkers([]);
      return;
    }
    setLoading(true);
    setError(null);
    let cancelled = false;
    createOrderV2Client
      .searchWorkers({
        ...baseParams,
        tab,
        search: debouncedSearch || undefined,
        page,
        per_page: PAGE_SIZE,
      })
      .then((result) => {
        if (cancelled) return;
        const list = result || [];
        // Seed the cache so the date-card invited avatars survive
        // pagination/tab/search changes (the next search may not return
        // the same worker on page 1).
        list.forEach((w) => invitedWorkersRef.current.set(w.id, w));
        setWorkers(list);
      })
      .catch((err) => {
        if (cancelled) return;
        setError((err as Error).message || 'Failed to load workers');
        setWorkers([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [baseParams, tab, debouncedSearch, page, showWorkerPanel]);

  useEffect(() => {
    if (!baseParams || !showWorkerPanel) {
      setCounts({});
      return;
    }
    let cancelled = false;
    createOrderV2Client
      .getWorkerCounts(baseParams)
      .then((result) => {
        if (!cancelled) setCounts(result || {});
      })
      .catch(() => {
        if (!cancelled) setCounts({});
      });
    return () => {
      cancelled = true;
    };
  }, [baseParams, showWorkerPanel]);

  const tabLabel = (value: WorkerTab, label: string) => {
    const map: Record<WorkerTab, number | undefined> = {
      all: counts.all ?? undefined,
      favourites: counts.favourites ?? undefined,
      gravy_trained: counts.trained ?? undefined,
      highly_rated: counts.highly_rated ?? undefined,
      past_workers: counts.past_workers ?? undefined,
      others: counts.others ?? undefined,
    };
    const count = map[value];
    return typeof count === 'number' ? `${label} (${count})` : label;
  };

  const skillName =
    skills?.find((s) => s.id === job?.skill_id)?.display_name_en ||
    skills?.find((s) => s.id === job?.skill_id)?.name ||
    'workers';

  // Mirrors v1's `getHiredUpdatedSchedules`: in v1 each Schedule fills
  // *independently* (`hiredWorkers.length === sch.quantity`) and a filled
  // schedule simply drops out of the active selection, so an unfilled
  // schedule still in selection keeps its Hire button live. V2 stores
  // hires flat per job, but every hire carries a `worker_shift_scope`
  // snapshot of the shifts that were selected when it was made. The cap
  // must therefore be evaluated *per currently-selected shift* against
  // only the hires scoped to that shift — not as a flat job total vs the
  // max shift quantity. Without this, hiring 3 for Shift 2 (q=3) while
  // Shift 1 (q=1) was deselected, then re-selecting Shift 1, left the
  // flat count (3) at/over the max quantity (3) and wrongly disabled
  // Hire even though Shift 1 still had an open position. A worker with
  // no scope entry (template/legacy drafts) applies to every selected
  // shift, matching buildCreatePayload's `workerAppliesToShift` fallback.
  const filledForShift = useCallback(
    (index: number) =>
      hireIds.reduce((count, workerId) => {
        const scope = workerShiftScope[workerId];
        const appliesHere = scope == null ? true : scope.includes(index);
        return appliesHere ? count + 1 : count;
      }, 0),
    [hireIds, workerShiftScope],
  );
  // Selected shifts that still have at least one unfilled position. A
  // new hire is scoped to exactly these (see toggleHire) so it can never
  // pile onto an already-full shift — the V2 analogue of v1 dropping a
  // filled schedule out of the active selection.
  const openSelectedShiftIndexes = useMemo(() => {
    const list = job?.shifts || [];
    const open: number[] = [];
    for (let i = 0; i < list.length; i += 1) {
      if (unselectedShifts.has(i)) continue;
      const required = Math.max(0, list[i]?.quantity ?? 0);
      if (required === 0) continue;
      if (filledForShift(i) < required) open.push(i);
    }
    return open;
  }, [job?.shifts, unselectedShifts, filledForShift]);
  // Whether any currently-selected shift defines a positive quantity at
  // all. When none do (all 0/undefined), the original code left the cap
  // unenforced — preserve that so legacy/quantity-less drafts behave as
  // before rather than hard-blocking Hire.
  const hasSelectedQuantity = useMemo(() => {
    const list = job?.shifts || [];
    for (let i = 0; i < list.length; i += 1) {
      if (unselectedShifts.has(i)) continue;
      if (Math.max(0, list[i]?.quantity ?? 0) > 0) return true;
    }
    return false;
  }, [job?.shifts, unselectedShifts]);
  const hireCapReached =
    hasSelectedQuantity && openSelectedShiftIndexes.length === 0;

  const renderWorkerRow = (worker: WorkerListItem) => {
    // V1 parity: evaluate the row state against the currently-selected
    // shift context only (see isAssignedToAllSelected), NOT a flat
    // job-level membership in inviteIds/hireIds. This is the core fix —
    // a worker hired only for Shift 1 must show "Hire" (not "Hired")
    // once Shift 2 is also selected, because they are not yet staffed
    // for the Shift 2 context.
    const inviteSelected = isAssignedToAllSelected(worker.id, inviteIds);
    const hireSelected = isAssignedToAllSelected(worker.id, hireIds);
    const conflict = !!worker.hasConflicts;
    // Mirrors v1's `Number(workerInfo.averageRating) > 0` check. The legacy
    // WorkerSerializer ships averageRating as a *string* (e.g. "4.5") in
    // some payloads, so a strict `typeof === 'number'` guard would treat
    // every rated worker as unrated — yielding a grey star and a "-"
    // placeholder. Coerce via Number(...) so genuine ratings render as
    // yellow stars with the numeric value.
    const ratingNumeric =
      worker.averageRating != null ? Number(worker.averageRating) : NaN;
    const rating =
      Number.isFinite(ratingNumeric) && ratingNumeric > 0
        ? ratingNumeric
        : null;
    const ratingCountNumeric =
      worker.jobsRatingCount != null ? Number(worker.jobsRatingCount) : NaN;
    const ratingCount =
      Number.isFinite(ratingCountNumeric) && ratingCountNumeric > 0
        ? ratingCountNumeric
        : null;
    const skillShifts =
      worker.selectedSkillShift != null ? Number(worker.selectedSkillShift) : 0;
    const shiftsWithYou =
      worker.shiftsWith != null ? Number(worker.shiftsWith) : 0;

    // Mirrors v1 enable rules in StaffingWorkerProfileRow.tsx:
    //  - Hire visible only when worker.hireable && (!isClientAdmin || isLta || allShiftsArePast)
    //  - Invite visible only when worker.invitable && (!isLta || hasConflicts),
    //    where `isLta` is v1's `isLTA || allSchedulesArePast()` — past
    //    GIG jobs collapse to LTA-style row behavior so Invite is
    //    suppressed (Hire stays).
    const showHire = !!worker.hireable && canShowHireButton;
    const showInvite =
      worker.invitable !== false && (!treatAsLtaForInvite || conflict);
    // Quantity-aware Hire disable: V1 drops a fully-staffed schedule
    // from selection once its hiredWorkers count reaches its quantity,
    // which disables the Hire button for additional workers. Mirror
    // that here against the per-shift quantity ceiling.
    const hireBlockedByCap = hireCapReached && !hireSelected;
    const hireDisabled =
      noShiftsSelected || (conflict && !hireSelected) || hireBlockedByCap;
    // V1 parity: once the required worker count is already hired,
    // both the Hire and Invite buttons should be disabled on remaining
    // rows — there are no positions left to fill. V2 left Invite
    // clickable, so the admin could accumulate invites that wouldn't
    // change staffing. An already-invited worker stays clickable so
    // the admin can free up a slot (mirrors `hireBlockedByCap`'s
    // `!hireSelected` carve-out).
    const inviteBlockedByCap = hireCapReached && !inviteSelected;
    const inviteDisabled =
      noShiftsSelected || hireSelected || inviteBlockedByCap;

    return (
      <WorkerRow key={worker.id}>
        <WorkerCell>
          <WorkerAvatarBubble worker={worker} size="lg" />
          <WorkerTextStack>
            <WorkerName title={formatName(worker)}>
              {formatName(worker)}
            </WorkerName>
            <WorkerMetaRow>
              <StarIcon filled={rating !== null} />
              <span style={{ color: rating !== null ? '#1C1C1C' : '#D1D5DB' }}>
                {rating !== null ? rating.toFixed(1) : '-'}
              </span>
              {ratingCount !== null ? <span>({ratingCount})</span> : null}
              {isTruthyFlag(worker.IsFavorite) ? (
                <>
                  <span style={{ color: '#D1D5DB' }}>|</span>
                  <FavoriteIcon />
                </>
              ) : null}
            </WorkerMetaRow>
          </WorkerTextStack>
        </WorkerCell>
        {/* v1 parity: TableCells render only the count — the "{Skill}
            Shifts" / "Shifts With You" labels live in the column header,
            never repeated per row. */}
        <ShiftStatCell>
          <strong>{skillShifts}</strong>
        </ShiftStatCell>
        <ShiftStatCell>
          <strong>{shiftsWithYou}</strong>
        </ShiftStatCell>
        <ShiftStatCell>
          <HoursThisWeekCell
            hoursThisWeek={worker.hoursThisWeek}
            selectedShiftHours={currentWeekSelectedShiftHours}
          />
        </ShiftStatCell>
        <ActionButtonsCell>
          {conflict ? <ConflictBadge>Conflict</ConflictBadge> : null}
          {showHire ? (
            <SelectionPill
              type="button"
              state={
                hireSelected
                  ? 'hired'
                  : conflict || hireBlockedByCap || noShiftsSelected
                    ? 'disabled'
                    : 'default'
              }
              disabled={hireDisabled}
              onClick={() => toggleHire(worker)}
            >
              {hireSelected ? 'Hired' : 'Hire'}
            </SelectionPill>
          ) : null}
          {showInvite ? (
            <SelectionPill
              type="button"
              state={
                inviteSelected
                  ? 'invited'
                  : hireSelected || noShiftsSelected || inviteBlockedByCap
                    ? 'disabled'
                    : 'default'
              }
              disabled={inviteDisabled}
              onClick={() => toggleInvite(worker)}
            >
              {inviteSelected ? 'Invited' : 'Invite'}
            </SelectionPill>
          ) : null}
        </ActionButtonsCell>
      </WorkerRow>
    );
  };

  const shifts = job?.shifts || [];
  const totalShifts = shifts.length;
  const selectedShiftCount = totalShifts - unselectedShifts.size;

  // Mirrors legacy `StaffingShiftsCardsSection`: each schedule card shows
  // the workers that have actually been invited (and hired) for it, read
  // straight from each worker's `worker_shift_scope` snapshot and
  // independent of the current selection (V1 renders schedule
  // .selectedWorkers / .hiredWorkers regardless of activeCardCheck — the
  // selection only drives the highlight/checkbox). A worker only appears
  // on a shift card whose index is in their captured snapshot — so hiring
  // while only shift 1 is selected, then selecting shift 2, leaves shift
  // 2 unstaffed (V1 parity). A missing snapshot (template-prefilled /
  // legacy drafts) means "every shift", so the worker shows on each card.
  const workersForShift = useCallback(
    (index: number) => {
      const cache = invitedWorkersRef.current;
      const lookup = (id: number): WorkerListItem =>
        cache.get(id) || ({ id } as WorkerListItem);
      const inScope = (id: number) => {
        const scope = workerShiftScope[id];
        if (scope == null) return true;
        return scope.includes(index);
      };
      return {
        invited: inviteIds.filter(inScope).map(lookup),
        hired: hireIds.filter(inScope).map(lookup),
      };
    },
    [inviteIds, hireIds, workerShiftScope],
  );
  const selectionState: 'none' | 'some' | 'all' =
    totalShifts === 0 || selectedShiftCount === 0
      ? 'none'
      : selectedShiftCount === totalShifts
        ? 'all'
        : 'some';

  // Mirror the local Set to `jobs[0].unselected_shift_indexes`. We
  // call this *after* the local setState, never inside a setState
  // updater — side effects in updaters fire twice in StrictMode and
  // can race with other writers (e.g. toggleInvite firing right
  // after the deselect click on a different worker row). The
  // submission bug reported on 2026-05-17 reproduces when the
  // persisted indexes are still empty by the time the user clicks
  // Invite, because the next `onUpdateJobs` spreads the stale job
  // and clobbers the deselection.
  const commitUnselected = (next: Set<number>) => {
    // Read from jobsRef so we always build on the freshest snapshot —
    // matches setStrategy/toggleCertificate/updateSelection above. The
    // closure-captured `jobs` could be stale within a single render
    // tick if multiple writers fire back-to-back, dropping the
    // unselected_shift_indexes the next writer just persisted.
    const latest = jobsRef.current;
    if (!latest[0]) return;
    const indexes = Array.from(next).sort((a, b) => a - b);
    const cur = latest[0].unselected_shift_indexes ?? [];
    const same =
      cur.length === indexes.length && cur.every((v, i) => v === indexes[i]);
    if (same) return;
    const nextJobs = [...latest];
    nextJobs[0] = { ...nextJobs[0], unselected_shift_indexes: indexes };
    onUpdateJobs(nextJobs);
  };

  const toggleShift = (index: number) => {
    // Compute next from the current local Set (synchronous read);
    // setState below uses the direct value, and we persist the same
    // value to job state in one batched render cycle.
    const next = new Set(unselectedShifts);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    setUnselectedShifts(next);
    commitUnselected(next);
  };

  // Tri-state header toggle:
  // - 'none' (no shifts selected): select every shift
  // - 'all' / 'some': deselect every shift
  // The 'some' branch matches the prototype where a partial selection still
  // shows "Deselect All" with a dash, and clicking clears the selection.
  const toggleSelectAll = () => {
    if (selectionState === 'none') {
      const empty = new Set<number>();
      setUnselectedShifts(empty);
      commitUnselected(empty);
      return;
    }
    const next = new Set<number>();
    for (let i = 0; i < totalShifts; i += 1) next.add(i);
    setUnselectedShifts(next);
    commitUnselected(next);
  };

  const renderInviteScreen = () => {
    if (!billingAccount) {
      return <MutedText>Select a billing account to load workers.</MutedText>;
    }
    if (!job?.skill_id) {
      return (
        <MutedText>
          Pick a skill on Step 1 to filter the worker list — counts and rows
          update once a skill is selected.
        </MutedText>
      );
    }
    return (
      <>
        <InviteHeader>Invite for {skillName}</InviteHeader>

        <ShiftsCard>
          <ShiftsHeaderRow>
            <SelectAllRow type="button" onClick={toggleSelectAll}>
              <Checkbox
                checked={selectionState === 'all'}
                indeterminate={selectionState === 'some'}
              >
                {selectionState === 'all' ? (
                  <CheckmarkIcon />
                ) : selectionState === 'some' ? (
                  <DashIcon />
                ) : null}
              </Checkbox>
              <span>
                {selectionState === 'none' ? 'Select All' : 'Deselect All'}
              </span>
            </SelectAllRow>
            <SelectionPillBadge>
              {selectionState === 'all'
                ? `All ${totalShifts} shifts selected`
                : `${selectedShiftCount} of ${totalShifts} shifts selected`}
            </SelectionPillBadge>
          </ShiftsHeaderRow>

          <DateCardsRow>
            {shifts.length === 0 ? (
              <MutedText>No shifts scheduled.</MutedText>
            ) : (
              shifts.map((shift, index) => {
                const selected = !unselectedShifts.has(index);
                const activeDow = dowIndex(shift.start_at);
                const cardWorkers = workersForShift(index);
                const cardHasWorkers =
                  cardWorkers.hired.length + cardWorkers.invited.length > 0;
                // V1 parity (StaffingShiftsCardsSection):
                // `hiredWorkersList.length > 0 ? length === quantity
                // : false`. Drives the green card border + the green
                // "Fully Staffed" suffix on the count label.
                const cardFullyStaffed =
                  cardWorkers.hired.length > 0 &&
                  cardWorkers.hired.length === shift.quantity;
                return (
                  <DateCard
                    key={index}
                    selected={selected}
                    fullyStaffed={cardFullyStaffed}
                  >
                    <DateCardHeader>
                      <DateLabel>{formatMonthDay(shift.start_at)}</DateLabel>
                      <SelectAllRow
                        type="button"
                        onClick={() => toggleShift(index)}
                        aria-label={
                          selected ? 'Deselect shift' : 'Select shift'
                        }
                      >
                        <Checkbox checked={selected}>
                          {selected ? <CheckmarkIcon /> : null}
                        </Checkbox>
                      </SelectAllRow>
                    </DateCardHeader>
                    <DowRow>
                      {DOW_LETTERS.map((letter, idx) => (
                        <DowDot key={idx} active={idx === activeDow}>
                          {letter}
                        </DowDot>
                      ))}
                    </DowRow>
                    <div>
                      <TimeText>
                        {formatHm(shift.start_at)} - {formatHm(shift.end_at)}
                      </TimeText>
                      {shift.break_minutes ? (
                        <BreakText>{shift.break_minutes} mins break</BreakText>
                      ) : null}
                    </div>
                    <InvitedRow
                      // V1 parity (StaffingShiftsCardsSection): the
                      // worker summary is clickable when the shift has
                      // workers — it opens the shift drill-in modal to
                      // inspect / cancel hires & invites for THIS shift.
                      role={cardHasWorkers ? 'button' : undefined}
                      tabIndex={cardHasWorkers ? 0 : undefined}
                      aria-label={
                        cardHasWorkers
                          ? `View workers for ${formatMonthDay(shift.start_at)}`
                          : undefined
                      }
                      style={cardHasWorkers ? { cursor: 'pointer' } : undefined}
                      onClick={
                        cardHasWorkers
                          ? () => setWorkerModalShift(index)
                          : undefined
                      }
                      onKeyDown={
                        cardHasWorkers
                          ? (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setWorkerModalShift(index);
                              }
                            }
                          : undefined
                      }
                    >
                      {(() => {
                        // V1 parity (StaffingShiftsCardsSection): the
                        // card always renders the workers actually
                        // assigned to *this* shift, read from each
                        // worker's worker_shift_scope snapshot. Selection
                        // state only drives the card highlight/checkbox —
                        // it must NOT blank retained hires/invites.
                        // Deselecting a shift previously forced an empty
                        // count here, making a retained assignment look
                        // removed (QA retest finding).
                        const invitedForCard = cardWorkers.invited;
                        const hiredForCard = cardWorkers.hired;
                        const label =
                          hiredForCard.length > 0
                            ? `${hiredForCard.length} Hired · ${invitedForCard.length} Invited`
                            : `${invitedForCard.length} Invited`;
                        const combined = [...hiredForCard, ...invitedForCard];
                        // Cap the row at 3 avatars and surface an
                        // overflow bubble for the rest. v2 previously
                        // silently dropped invitations 4+, so users
                        // couldn't tell when their later picks had
                        // registered — see the "more than 3 workers
                        // invited" bug report. The bubble shows "+N"
                        // and exposes the total via its title attr.
                        const MAX_AVATARS = 3;
                        const avatars = combined.slice(0, MAX_AVATARS);
                        const overflow = Math.max(
                          0,
                          combined.length - MAX_AVATARS,
                        );
                        return (
                          <>
                            <span aria-label="Invited workers count">
                              {label}
                              {cardFullyStaffed ? (
                                <span style={{ color: '#45A735' }}>
                                  {' · Fully Staffed'}
                                </span>
                              ) : null}
                            </span>
                            {combined.length === 0 ? (
                              <AvatarPlaceholder aria-hidden />
                            ) : (
                              <span
                                aria-hidden
                                style={{ display: 'inline-flex', gap: 2 }}
                              >
                                {avatars.map((w) => (
                                  <WorkerAvatarBubble
                                    key={w.id}
                                    worker={w}
                                    size="sm"
                                    title={
                                      w.user?.firstName || `Worker #${w.id}`
                                    }
                                  />
                                ))}
                                {overflow > 0 ? (
                                  <AvatarOverflowBubble
                                    aria-hidden
                                    title={`${overflow} more`}
                                  >
                                    +{overflow}
                                  </AvatarOverflowBubble>
                                ) : null}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </InvitedRow>
                  </DateCard>
                );
              })
            )}
          </DateCardsRow>
        </ShiftsCard>

        <WorkerPanel>
          <PanelTitleRow>
            {/*
              V1 parity: legacy CreateGigOrder switches the worker
              panel heading to "LTA GravyWorkers" for LTA orders. The
              tabs row (All / Favorites / GravyTrained / Highly rated /
              Past workers / Others) is also hidden for LTA, since
              those segment buckets are gig-specific. See the legacy
              StaffingTabs + worker-list title gates.
            */}
            <PanelTitle>
              {isLta ? 'LTA GravyWorkers' : 'All GravyWorkers'}
            </PanelTitle>
            <SearchWrap>
              <SearchIconAbs>
                <SearchIconSvg />
              </SearchIconAbs>
              <SearchInput
                aria-label="Search GravyWorkers"
                placeholder="Search for GravyWorkers"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </SearchWrap>
          </PanelTitleRow>
          {!isLta && (
            <TabsRow>
              {workerTabs.map(({ label, value }) => (
                <TabButton
                  key={value}
                  type="button"
                  active={tab === value}
                  onClick={() => setTab(value)}
                >
                  {tabLabel(value, label)}
                </TabButton>
              ))}
            </TabsRow>
          )}
          {selectedShiftHours > 0 ? (
            <div style={{ padding: '12px 24px 0' }}>
              <OTRiskBanner shiftHours={selectedShiftHours} />
            </div>
          ) : null}
          <HeaderRow>
            <span>Worker Details</span>
            <span>{skillName ? `${skillName} Shifts` : 'Shifts'}</span>
            <span>Shifts With You</span>
            <span>Hours This Week</span>
            <span aria-hidden />
          </HeaderRow>
          {noShiftsSelected ? (
            <div
              role="alert"
              style={{
                margin: '8px 24px 0',
                padding: '10px 12px',
                borderRadius: 6,
                background: '#FDECEC',
                border: '1px solid #F5B7B7',
                color: '#A41B1B',
                fontSize: 13,
                lineHeight: 1.4,
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              <strong>Select at least one shift</strong> before assigning
              workers — Hire and Invite are disabled until a shift is selected.
            </div>
          ) : null}
          {error ? (
            <ErrorText style={{ padding: '12px 24px' }}>{error}</ErrorText>
          ) : null}
          {loading && workers.length === 0 ? (
            <MutedText style={{ padding: '14px 24px' }}>
              Loading workers…
            </MutedText>
          ) : workers.length === 0 ? (
            <MutedText style={{ padding: '14px 24px' }}>
              No workers match this filter.
            </MutedText>
          ) : (
            workers.map(renderWorkerRow)
          )}
          <PaginationRow>
            <MutedText>
              {inviteIds.length} invited · {hireIds.length} hired
            </MutedText>
            <div style={{ display: 'flex', gap: 8 }}>
              <PageButton
                type="button"
                disabled={page === 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </PageButton>
              <PageButton
                type="button"
                disabled={loading || workers.length < PAGE_SIZE}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </PageButton>
            </div>
          </PaginationRow>
        </WorkerPanel>
      </>
    );
  };

  // Build the shift drill-in modal's props from the open shift index.
  // Re-derived each render so a cancel inside the modal immediately
  // reflows the lists (and empties them -> the modal self-closes).
  const toShiftWorkerRow = (worker: WorkerListItem): ShiftWorkerRow => ({
    id: worker.id,
    name: formatName(worker),
    initial: initialFor(worker),
    avatarUrl: worker.avatarUrl ?? null,
  });
  const modalShift =
    workerModalShift != null ? shifts[workerModalShift] : undefined;
  const modalWorkers =
    workerModalShift != null ? workersForShift(workerModalShift) : null;

  return (
    <StepContent>
      <ContentWrap wide={substep === 'invite'}>
        {substep === 'invite' ? (
          renderInviteScreen()
        ) : (
          <>
            <StepTitle>How would you like to staff your shifts?</StepTitle>

            {!isLta && (
              <ChoiceSection>
                <ChoiceCard
                  role="button"
                  tabIndex={0}
                  aria-pressed={strategy === 'OPEN_JOB_POST'}
                  selected={strategy === 'OPEN_JOB_POST'}
                  onClick={() => setStrategy('OPEN_JOB_POST')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setStrategy('OPEN_JOB_POST');
                    }
                  }}
                >
                  <ChoiceIcon>
                    <OpenJobIcon />
                  </ChoiceIcon>
                  <ChoiceBody>
                    <ChoiceTitle>Open job post</ChoiceTitle>
                    <ChoiceDescription>
                      Make your job publicly available to attract a wide pool of
                      qualified candidates.
                    </ChoiceDescription>
                  </ChoiceBody>
                  <ChoiceRadio selected={strategy === 'OPEN_JOB_POST'} />
                </ChoiceCard>

                <ChoiceCard
                  role="button"
                  tabIndex={0}
                  aria-pressed={strategy === 'SEND_INVITATIONS'}
                  selected={strategy === 'SEND_INVITATIONS'}
                  onClick={() => setStrategy('SEND_INVITATIONS')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setStrategy('SEND_INVITATIONS');
                    }
                  }}
                >
                  <ChoiceIcon>
                    <InviteIcon />
                  </ChoiceIcon>
                  <ChoiceBody>
                    <ChoiceTitle>Send invitations</ChoiceTitle>
                    <ChoiceDescription>
                      Send job invites to specific workers and ensure the best
                      fit for your needs.
                    </ChoiceDescription>
                    <AutoFillCard
                      onClick={(event) => event.stopPropagation()}
                      style={{ marginTop: 12 }}
                    >
                      <AutoFillText>
                        Make the job available to qualified workers if your
                        selected workers are unavailable.
                      </AutoFillText>
                      <ToggleSwitch
                        type="button"
                        on={autoFill}
                        onClick={(event) => {
                          event.stopPropagation();
                          setAutoFill(!autoFill);
                        }}
                        aria-pressed={autoFill}
                        aria-label="Make the job available to qualified workers if your selected workers are unavailable"
                      >
                        <ToggleKnob on={autoFill} />
                      </ToggleSwitch>
                    </AutoFillCard>
                  </ChoiceBody>
                  <ChoiceRadio selected={strategy === 'SEND_INVITATIONS'} />
                </ChoiceCard>
              </ChoiceSection>
            )}

            {/*
              V1 parity + redesign: the legacy CreateGigOrder flow hides
              the required-badges picker for Customer/Client Admins —
              the backend applies the customer's default badge set for
              that role, so exposing the picker here was both misleading
              and out of sync with order creation. Tenant Admins see the
              redesigned "Badge Requirements" dropdown: a single select
              trigger that opens a list of badge rows with checkboxes.
              Slotted below the Open job post / Send invitations cards
              (and the LTA-only single-strategy view) per the redesign.
            */}
            {!isClientAdmin && certificateOptions.length > 0 && (
              <CertificateCard>
                <CertificateTitle>Badge Requirements</CertificateTitle>
                <BadgeDropdownWrap ref={badgeWrapRef}>
                  <BadgeDropdownTrigger
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={badgeOpen}
                    onClick={() => setBadgeOpen((open) => !open)}
                  >
                    <BadgeDropdownTriggerLabel
                      hasSelection={certificateIds.length > 0}
                    >
                      {certificateIds.length === 0
                        ? 'Select Badges'
                        : certificateOptions
                            .filter((cert) => certificateIds.includes(cert.id))
                            .map((cert) => cert.name)
                            .join(', ')}
                    </BadgeDropdownTriggerLabel>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#1C1C1C"
                      strokeWidth={2}
                      width={20}
                      height={20}
                      style={{
                        flexShrink: 0,
                        transform: badgeOpen
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                        transition: 'transform 0.15s',
                      }}
                      aria-hidden
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </BadgeDropdownTrigger>
                  {badgeOpen && (
                    <BadgeDropdownPopover role="listbox">
                      {certificateOptions.map((cert) => {
                        const selected = certificateIds.includes(cert.id);
                        return (
                          <BadgeOptionRow
                            key={cert.id}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            onClick={() => toggleCertificate(cert.id)}
                          >
                            <Checkbox checked={selected}>
                              {selected ? <CheckmarkIcon /> : null}
                            </Checkbox>
                            <span>{cert.name}</span>
                          </BadgeOptionRow>
                        );
                      })}
                    </BadgeDropdownPopover>
                  )}
                </BadgeDropdownWrap>
              </CertificateCard>
            )}
          </>
        )}
      </ContentWrap>

      <SendInvitationsModal
        isOpen={inviteOpen}
        onCancel={() => setInviteOpen(false)}
        onContinue={() => setInviteOpen(false)}
      />

      <ShiftWorkersModal
        isOpen={workerModalShift != null && !!modalShift}
        onClose={() => setWorkerModalShift(null)}
        dateLabel={modalShift ? formatMonthDay(modalShift.start_at) : ''}
        timeLabel={
          modalShift
            ? `${formatHm(modalShift.start_at)} - ${formatHm(modalShift.end_at)}`
            : ''
        }
        quantity={modalShift?.quantity}
        hired={(modalWorkers?.hired ?? []).map(toShiftWorkerRow)}
        invited={(modalWorkers?.invited ?? []).map(toShiftWorkerRow)}
        isClientAdmin={isClientAdmin}
        isLta={isLta}
        onCancelWorkers={(list, workerIds) => {
          if (workerModalShift == null) return;
          cancelWorkersForShift(list, workerIds, workerModalShift);
        }}
      />
      {overtimeModal}
    </StepContent>
  );
};

export default StaffingStep;
